import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser, saveUserWorkoutPlans, saveUserMeasurements, saveUserSettings } from '../utils/supabaseDataManager';
import { getAssessmentWeek } from '../data/assessmentWeeks';
import './Onboarding.css';

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    username: '',
    displayName: '',
    age: '',
    gender: '',
    height: '',
    weight: '',

    // Step 2: Fitness Level
    fitnessLevel: '',
    pullups: '',
    pushups: '',
    bodyFat: '',

    // Step 3: Goals
    primaryGoal: '',
    targetAreas: [],
    timeframe: '',

    // Step 4: Availability
    daysPerWeek: '',
    timePerSession: '',
    homeWorkoutTime: 'early_morning',
    gymWorkoutTime: 'morning',
    gymDays: [], // Preferred days for gym workouts
    restDays: [], // Preferred rest days
    gymAccess: [],
    equipment: []
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Create user profile in Supabase
      const userProfile = {
        username: formData.username.toLowerCase(),
        displayName: formData.displayName,
        age: parseInt(formData.age),
        gender: formData.gender,
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        fitnessLevel: formData.fitnessLevel,
        pullups: parseInt(formData.pullups) || 0,
        pushups: parseInt(formData.pushups) || 0,
        bodyFat: parseFloat(formData.bodyFat) || null,
        primaryGoal: formData.primaryGoal,
        targetAreas: formData.targetAreas,
        timeframe: formData.timeframe,
        daysPerWeek: parseInt(formData.daysPerWeek),
        timePerSession: formData.timePerSession,
        homeWorkoutTime: formData.homeWorkoutTime,
        gymWorkoutTime: formData.gymWorkoutTime,
        gymDays: formData.gymDays,
        restDays: formData.restDays,
        gymAccess: formData.gymAccess,
        equipment: formData.equipment
      };

      const result = await createUser(userProfile);

      if (!result.success) {
        throw new Error(result.error.message || 'Failed to create user');
      }

      // Set as current user
      localStorage.setItem('currentUser', userProfile.username);

      // Get Week 1 Assessment Plan based on user's goal, equipment, gym days, and gym access
      const assessmentWeek = getAssessmentWeek(
        formData.primaryGoal,
        formData.equipment,
        formData.gymDays,
        formData.gymAccess
      );

      // Save assessment week as initial workout plan
      await saveUserWorkoutPlans(assessmentWeek);

      // Save plan metadata - mark as assessment week
      await saveUserSettings({
        lastPlanUpdate: new Date().toISOString(),
        lastPlanSummary: `Week 1: ${assessmentWeek.programName} - Gathering baseline data for AI personalization`,
        isAssessmentWeek: true,
        assessmentWeekNumber: 1
      });

      // Also save to localStorage for WeeklyReview to use
      localStorage.setItem('isAssessmentWeek', 'true');
      localStorage.setItem('assessmentWeekNumber', '1');

      // Save initial measurements
      await saveUserMeasurements({
        weight: formData.weight,
        bodyFat: formData.bodyFat,
        pullups: formData.pullups,
        pushups: formData.pushups,
        date: new Date().toISOString()
      });

      // Navigate to home
      navigate('/');
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="onboarding-screen">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2 className="loading-title">📋 Setting up your Week 1 Assessment...</h2>
          <p className="loading-text">
            Creating a personalized assessment week based on your goal: {formData.primaryGoal}
          </p>
          <p className="loading-subtext">Week 1 will gather baseline data so the AI can design your perfect program starting Week 2...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-screen">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <h1 className="onboarding-logo">💪 Functional Strength</h1>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(step / 4) * 100}%` }}></div>
          </div>
          <p className="progress-text">Step {step} of 4</p>
        </div>

        <div className="onboarding-card">
          {step === 1 && (
            <div className="onboarding-step">
              <h2 className="step-title">👋 Let's get to know you</h2>
              <p className="step-subtitle">Basic information to personalize your experience</p>

              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  placeholder="jesse"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  placeholder="Jesse"
                  value={formData.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Age</label>
                  <input
                    type="number"
                    placeholder="42"
                    value={formData.age}
                    onChange={(e) => handleChange('age', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <select value={formData.gender} onChange={(e) => handleChange('gender', e.target.value)}>
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Height (inches)</label>
                  <input
                    type="number"
                    placeholder="70"
                    value={formData.height}
                    onChange={(e) => handleChange('height', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Weight (lbs)</label>
                  <input
                    type="number"
                    placeholder="175"
                    value={formData.weight}
                    onChange={(e) => handleChange('weight', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="onboarding-step">
              <h2 className="step-title">💪 Current Fitness Level</h2>
              <p className="step-subtitle">Help us understand where you're starting from</p>

              <div className="form-group">
                <label>Overall Fitness Level</label>
                <select value={formData.fitnessLevel} onChange={(e) => handleChange('fitnessLevel', e.target.value)}>
                  <option value="">Select</option>
                  <option value="beginner">Beginner (New to exercise)</option>
                  <option value="intermediate">Intermediate (Regular exerciser)</option>
                  <option value="advanced">Advanced (Very experienced)</option>
                  <option value="returning">Returning (Previously fit, getting back)</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Max Pull-ups</label>
                  <input
                    type="number"
                    placeholder="0-20"
                    value={formData.pullups}
                    onChange={(e) => handleChange('pullups', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Max Push-ups</label>
                  <input
                    type="number"
                    placeholder="0-50"
                    value={formData.pushups}
                    onChange={(e) => handleChange('pushups', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Body Fat % (optional, estimate if unsure)</label>
                <input
                  type="number"
                  placeholder="15-30"
                  value={formData.bodyFat}
                  onChange={(e) => handleChange('bodyFat', e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="onboarding-step">
              <h2 className="step-title">🎯 Your Goals</h2>
              <p className="step-subtitle">What do you want to achieve?</p>

              <div className="form-group">
                <label>Primary Goal</label>
                <select value={formData.primaryGoal} onChange={(e) => handleChange('primaryGoal', e.target.value)}>
                  <option value="">Select</option>
                  <option value="strength">Build Functional Strength</option>
                  <option value="muscle">Build Muscle Mass</option>
                  <option value="toned">Get Toned & Defined</option>
                  <option value="athletic">Improve Athletic Performance</option>
                  <option value="weight_loss">Lose Weight & Get Fit</option>
                  <option value="general_fitness">General Fitness & Health</option>
                </select>
              </div>

              <div className="form-group">
                <label>Target Areas (select all that apply)</label>
                <div className="checkbox-grid">
                  {['arms', 'chest', 'shoulders', 'back', 'core', 'glutes', 'thighs', 'calves', 'full_body'].map(area => (
                    <label key={area} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.targetAreas.includes(area)}
                        onChange={() => handleArrayToggle('targetAreas', area)}
                      />
                      <span>{area.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Goal Timeframe</label>
                <select value={formData.timeframe} onChange={(e) => handleChange('timeframe', e.target.value)}>
                  <option value="">Select</option>
                  <option value="8_weeks">8 weeks</option>
                  <option value="12_weeks">12 weeks</option>
                  <option value="6_months">6 months</option>
                  <option value="1_year">1 year</option>
                  <option value="lifestyle">Ongoing lifestyle</option>
                </select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="onboarding-step">
              <h2 className="step-title">📅 Training Schedule</h2>
              <p className="step-subtitle">When and where will you train?</p>

              <div className="form-row">
                <div className="form-group">
                  <label>Days Per Week</label>
                  <select value={formData.daysPerWeek} onChange={(e) => handleChange('daysPerWeek', e.target.value)}>
                    <option value="">Select</option>
                    <option value="3">3 days</option>
                    <option value="4">4 days</option>
                    <option value="5">5 days</option>
                    <option value="6">6 days</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Workout Duration Range</label>
                  <select value={formData.timePerSession} onChange={(e) => handleChange('timePerSession', e.target.value)}>
                    <option value="">Select</option>
                    <option value="under_30">Less than 30 minutes</option>
                    <option value="30_45">30-45 minutes</option>
                    <option value="45_60">45-60 minutes</option>
                    <option value="60_plus">60+ minutes</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Preferred Workout Times (different days can have different times)</label>
                <div className="time-preferences">
                  <p className="time-hint">💡 Home workouts and gym workouts can be at different times</p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Home Workouts</label>
                      <select value={formData.homeWorkoutTime || 'early_morning'} onChange={(e) => handleChange('homeWorkoutTime', e.target.value)}>
                        <option value="early_morning">Early Morning (5-7 AM)</option>
                        <option value="morning">Morning (7-10 AM)</option>
                        <option value="midday">Midday (10 AM-2 PM)</option>
                        <option value="afternoon">Afternoon (2-6 PM)</option>
                        <option value="evening">Evening (6-9 PM)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Gym Workouts</label>
                      <select value={formData.gymWorkoutTime || 'morning'} onChange={(e) => handleChange('gymWorkoutTime', e.target.value)}>
                        <option value="early_morning">Early Morning (5-7 AM)</option>
                        <option value="morning">Morning (7-10 AM)</option>
                        <option value="midday">Midday (10 AM-2 PM)</option>
                        <option value="afternoon">Afternoon (2-6 PM)</option>
                        <option value="evening">Evening (6-9 PM)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Gym Access (select all)</label>
                <div className="checkbox-grid">
                  {['planet_fitness', 'home_gym', 'commercial_gym', 'other_gym', 'no_gym'].map(gym => (
                    <label key={gym} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.gymAccess.includes(gym)}
                        onChange={() => handleArrayToggle('gymAccess', gym)}
                      />
                      <span>{gym.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.gymAccess.length > 0 && !formData.gymAccess.includes('no_gym') && (
                <div className="form-group">
                  <label>Preferred Gym Days</label>
                  <p className="field-hint">Which days do you prefer to go to the gym?</p>
                  <div className="checkbox-grid">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                      <label key={day} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.gymDays.includes(day)}
                          onChange={() => handleArrayToggle('gymDays', day)}
                        />
                        <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Preferred Rest Days</label>
                <p className="field-hint">Which days do you prefer to rest? (Select up to {7 - parseInt(formData.daysPerWeek || 3)} days)</p>
                <div className="checkbox-grid">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <label key={day} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.restDays.includes(day)}
                        onChange={() => handleArrayToggle('restDays', day)}
                      />
                      <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Home Equipment (if any)</label>
                <div className="checkbox-grid">
                  {['dumbbells', 'kettlebells', 'weight_bench', 'dip_station', 'resistance_bands', 'pull_up_bar', 'yoga_mat', 'peloton', 'treadmill', 'none'].map(equip => (
                    <label key={equip} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.equipment.includes(equip)}
                        onChange={() => handleArrayToggle('equipment', equip)}
                      />
                      <span>{equip.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="error-box">
              <p>⚠️ {error}</p>
              {error.includes('API key') && (
                <p className="error-hint">Please add your Claude API key in Settings after setup.</p>
              )}
            </div>
          )}

          <div className="button-row">
            {step > 1 && (
              <button className="btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={step === 1 && (!formData.username || !formData.displayName)}
            >
              {loading ? '⏳ Generating...' : step === 4 ? '🚀 Create My Program' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
